import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FaEye, FaSearch } from "react-icons/fa";
import { DatePicker } from "antd";
import dayjs from "dayjs";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState } from "../../../components/shared/GlobalState";
import EventView from "../../Event/List/EventView";
import useFetch from "../../../components/shared/useFetch";
import placeholderImage from "../../../images/placeholder.png";

const { RangePicker } = DatePicker;

const EventsReportsChild = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [eventData, setEventData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [{ errorLogApi, user }] = useAtom(gbState);
  const [dateRange, setDateRange] = useState([]);

  let urlLink = `${process.env.REACT_APP_API_URL}/event/${user.church_id}`;
  const { data, loading, error, refetch } = useFetch(urlLink);

  useEffect(() => {
    if (data?.events) {
      const updatedData = data.events.map((event) => ({
        ...event,
        id: event._id,
        view_event_start_date: event.event_start_date
          ? dayjs(event.event_start_date).format("MM-DD-YYYY") +
            " " +
            event.event_start_time
          : "",
        view_event_end_date: event.event_end_date
          ? dayjs(event.event_end_date).format("MM-DD-YYYY") +
            " " +
            event.event_end_time
          : "",
      }));
      setEventData(updatedData);
    }
  }, [data]);

  const filteredData = useMemo(() => {
    return eventData.filter((event) => {
      const matchesSearch = Object.values(event).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );

      const eventStartDate = dayjs(event.event_start_date);
      const isWithinDateRange =
        dateRange.length === 0 ||
        (eventStartDate.isAfter(dateRange[0].subtract(1, "day")) &&
          eventStartDate.isBefore(dateRange[1].add(1, "day")));

      return matchesSearch && isWithinDateRange;
    });
  }, [eventData, searchTerm, dateRange]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Events Reporting</h2>
      </div>

      <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search events..."
          InputProps={{
            startAdornment: <FaSearch className="mr-2 text-gray-400" />,
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <RangePicker
          onChange={(dates) => {
            setDateRange(
              dates ? [dates[0].startOf("day"), dates[1].endOf("day")] : []
            );
          }}
          style={{ width: "100%" }}
        />
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Image</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Start Date & Time</TableCell>
              <TableCell>End Date & Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <div className="flex items-center space-x-1">
                    <Tooltip title="View details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setShowModal(true);
                          setCurrentRecord(row);
                        }}
                      >
                        <FaEye className="text-blue-600" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell>
                  <img
                    src={
                      row.image
                        ? `${process.env.REACT_APP_REPORTS_DOWNLOAD_URL}uploads/event/${row.image}`
                        : placeholderImage
                    }
                    alt="Event"
                    className="w-[55px] p-1 mr-1 rounded-lg"
                    crossOrigin="anonymous"
                  />
                </TableCell>
                <TableCell>{row.title}</TableCell>
                <TableCell>{row.view_event_start_date}</TableCell>
                <TableCell>{row.view_event_end_date}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <div className="flex items-center justify-between mt-4">
        <div>
          <span>Rows per page: </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Button
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <Button
            disabled={paginatedData.length < rowsPerPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Modal */}
      <ErrorBoundaryRoutes route="/events" page="ModalView">
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <EventView currentRecord={currentRecord} />
          </Modal>
        )}
      </ErrorBoundaryRoutes>
    </div>
  );
};

export default EventsReportsChild;
